import { useLazyQuery, useQuery } from "@apollo/client/react";
import { useState, useEffect, useMemo } from 'react';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';

import GET_HONORARIOS_VISITA, { adaptHonorariosVisita } from '../../../data/graphQL/queries/ControlVisitas/getHonorariosVisita';
import { RenderInputByTypeMobile } from '../../../components/formGroupGenerator/RenderInputByTypeMobile';
import { GET_VISITA, GET_VISITA_PRESENCIAL_AE } from '../../../data/graphQL/queries/MisVisitas/getVisita';
import { GET_FRECUENCIA_BY_VISITA } from '../../../data/graphQL/queries/MisVisitas/getFrecuenciaByVisita';
import ES_ENFERMERO_CUIDADOR from '../../../data/graphQL/queries/MisVisitas/esEnfermeroCuidadorAuxiliar';
import { GET_FASE_PACIENTE } from '../../../data/graphQL/queries/ControlVisitas/getFasePaciente';
import { GET_VISITA_EXCEDE_CAPACITACIONES } from '../../../data/graphQL/queries/ControlVisitas/getVisitaExcedeCapacitaciones';
import { diffMinutes, calculaPrecioExtraDuracion, calculaPrecioNormalCuidador } from '../constants/helpers';
import { aplicarReglaFase } from '../helpers/reglaFase';

const ID_URGENCIA = 4;
const ID_ENFERMERO_CUIDADOR = 7;
const ID_ENFERMERO = 2;

export const GrillaHonorario = ({ visita, setImportes, idTipoProfesional }) => {
  const [honorario, setHonorario] = useState({});
  const [visitaPresencialAE, setVisitaPresencialAE] = useState({});
  const [excedeCapacitaciones, setExcedeCapacitaciones] = useState(false);
  const [esCuidador, setEsCuidador] = useState(false);

  useEffect(() => {
    setHonorario((prevDatos) => ({
      ...prevDatos,
      checkCapacitacion: [ID_ENFERMERO, ID_ENFERMERO_CUIDADOR].includes(idTipoProfesional) && !!visita?.capacitacion && !excedeCapacitaciones,
      checkCambioSonda: [ID_ENFERMERO, ID_ENFERMERO_CUIDADOR].includes(idTipoProfesional) && !!visitaPresencialAE?.cambioSonda,
      checkEmergencia: [ID_ENFERMERO, ID_ENFERMERO_CUIDADOR].includes(idTipoProfesional) && visita?.idComboVisitaValorFormaVisita == ID_URGENCIA,
      checkExtraDuracion: [ID_ENFERMERO, ID_ENFERMERO_CUIDADOR].includes(idTipoProfesional) && diffMinutes(visita?.fechaVisita, visita?.fechaFinUsuario) > 45,
    }));
  }, [visita, visitaPresencialAE, excedeCapacitaciones]);

  const disabledChecks = useMemo(() => {
    return {
      disabledCambioSonda: ![ID_ENFERMERO, ID_ENFERMERO_CUIDADOR].includes(idTipoProfesional) || !visitaPresencialAE?.cambioSonda,
      disabledCapacitacion: ![ID_ENFERMERO, ID_ENFERMERO_CUIDADOR].includes(idTipoProfesional) || !visita?.capacitacion || excedeCapacitaciones,
      disabledEmergencia: ![ID_ENFERMERO, ID_ENFERMERO_CUIDADOR].includes(idTipoProfesional) || !visita?.forma?.includes('Urgencia'),
      disabledExtraDuracion: ![ID_ENFERMERO, ID_ENFERMERO_CUIDADOR].includes(idTipoProfesional) || diffMinutes(visita?.fechaVisita, visita?.fechaFinUsuario) <= 45,
    }
  }, [visita, visitaPresencialAE, excedeCapacitaciones]);

  const handleCheck = (name, checked) => {
    setHonorario((prev) => ({
      ...prev,
      [name]: checked
    }));
  };

  const { data: dataVisitaPresencialAE } = useQuery(GET_VISITA_PRESENCIAL_AE, {
    fetchPolicy: 'no-cache',
    variables: { idVisita: visita?.idVisita }
  });

  const { data: dataFrecuenciaByVisita } = useQuery(GET_FRECUENCIA_BY_VISITA, {
    fetchPolicy: 'no-cache',
    variables: { idVisita: visita?.idVisita }
  });

  const { data: dataExcedeCapacitaciones } = useQuery(GET_VISITA_EXCEDE_CAPACITACIONES, {
    fetchPolicy: 'no-cache',
    variables: { idVisita: visita?.idVisita }
  });

  useEffect(() => {
    if (!dataVisitaPresencialAE) return;
    setVisitaPresencialAE(dataVisitaPresencialAE?.visitaPresencialAE || {});
  }, [dataVisitaPresencialAE]);

  useEffect(() => {
    if (!dataExcedeCapacitaciones) return;
    setExcedeCapacitaciones(dataExcedeCapacitaciones?.visitaExcedeCantidadCapacitaciones || false);
  }, [dataExcedeCapacitaciones]);

  const { data: dataEsEnfermoCuidador } = useQuery(ES_ENFERMERO_CUIDADOR, {
    fetchPolicy: 'no-cache',
    variables: { idVisita: visita?.idVisita }
  });

  useEffect(() => {
    if (!dataEsEnfermoCuidador) return;
    setEsCuidador(!!dataEsEnfermoCuidador?.esEnfermeroCuidador);
  }, [dataEsEnfermoCuidador]);

  const [getFasePaciente, { data: dataFasePaciente }] = useLazyQuery(GET_FASE_PACIENTE, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (visita?.idEvaluacion) {
      getFasePaciente({ variables: { idEvaluacion: visita.idEvaluacion } });
    }
  }, [visita?.idEvaluacion]);

  const fase = dataFasePaciente?.fasePaciente ?? 0;

  useEffect(() => {
    const frecuencia = dataFrecuenciaByVisita?.frecuenciaByVisita?.frecuencia ?? 0;

    const importeTipo = esCuidador
      ? calculaPrecioNormalCuidador(visita.fechaVisita, visita.fechaFinUsuario, honorario?.honorarioPresencial, frecuencia ?? 0)
      : visita?.tipo === 'Presencial' ? honorario?.honorarioPresencial : honorario?.honorarioVirtual;

    const valorPresencial = visita?.tipo === 'Presencial' ? honorario?.honorarioPresencial : 0;
    const valorVirtual = visita?.tipo !== 'Presencial' ? honorario?.honorarioVirtual : 0;
    const valorTramo = visita?.tipo === 'Presencial' ? honorario?.tramo : 0;
    const valorCapacitacion = !!honorario?.checkCapacitacion && !!visita?.capacitacion && !excedeCapacitaciones ? honorario?.capacitacion : 0;
    const valorEmergencia = !!honorario?.checkEmergencia && visita?.idComboVisitaValorFormaVisita == ID_URGENCIA ? honorario?.emergencia : 0;
    const valorCambioSonda = !!honorario?.checkCambioSonda && !!visitaPresencialAE?.cambioSonda ? honorario?.cambioSonda : 0;
    const valorExtraDuracion = !honorario?.checkExtraDuracion ? 0
      : idTipoProfesional == ID_ENFERMERO
        ? honorario.extraDuracion
        : calculaPrecioExtraDuracion(visita.fechaVisita, visita.fechaFinUsuario, honorario?.honorarioPresencial, esCuidador, frecuencia ?? 0);

    const calculatedTotal = importeTipo + valorTramo + valorCapacitacion + valorCambioSonda + valorEmergencia + valorExtraDuracion;

    const importes = {
      presencial: valorPresencial,
      virtual: valorVirtual,
      tramo: valorTramo,
      capacitacion: valorCapacitacion,
      cambioSonda: valorCambioSonda,
      emergencia: valorEmergencia,
      extraDuracion: valorExtraDuracion,
      importeTotal: calculatedTotal
    };

    const importesFinales = aplicarReglaFase({
      importes: importes,
      fase,
      idTipoModulo: visita?.idTipoModulo,
      idTipoProfesional: visita?.idTipoProfesional,
      idComboVisitaValorFormaVisita: visita?.idComboVisitaValorFormaVisita,
      idComboVisitaValorPacienteDomicilio: visita?.idComboVisitaValorPacienteDomicilio,
      honorarioPresencial: honorario?.honorarioPresencial,
      honorarioVirtual: honorario?.honorarioVirtual
    });

    setImportes((prev) => ({ ...prev, ...importesFinales }));
    setHonorario((prev) => ({ ...prev, total: importesFinales.importeTotal, }));
    
  }, [
    visita?.tipo,
    visita?.fechaVisita,
    visita?.fechaFinUsuario,
    visita?.idProfesionalNavigation,
    visita?.idTipoModulo,
    visita?.idComboVisitaValorPacienteDomicilio,
    honorario?.honorarioPresencial,
    honorario?.honorarioVirtual,
    honorario?.checkCapacitacion,
    honorario?.checkCambioSonda,
    honorario?.checkEmergencia,
    honorario?.checkExtraDuracion,
    visitaPresencialAE,
    excedeCapacitaciones,
    esCuidador,
    dataFrecuenciaByVisita,
    fase
  ]);

  const columnsBase = [
    {
      field: 'honorarioPresencial',
      headerName: 'H. Presencial',
      flex: 1,
      valueGetter: (honorarioPresencial) => { return `$${honorarioPresencial || 0}`; }
    },
    {
      field: 'honorarioVirtual',
      headerName: 'H. Virtual',
      flex: 1,
      valueGetter: (honorarioVirtual) => { return `$${honorarioVirtual || 0}`; }
    },
    {
      field: 'tramo',
      headerName: 'Tramo',
      flex: 1,
      valueGetter: (tramo) => { return `$${tramo || 0}`; }
    },
    {
      field: 'cambioSonda',
      headerName: 'Cbio. Sonda',
      flex: 1,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return <RenderInputByTypeMobile
          input={{
            disableTypography: true,
            displayName: `$${row?.cambioSonda || 0}`,
            name: 'checkCambioSonda',
            type: 'check',
            disabled: disabledChecks?.disabledCambioSonda
          }}
          value={row?.checkCambioSonda}
          handleChange={(name, value) => handleCheck(name, value)} />;

      }
    },
    {
      field: 'capacitacion',
      headerName: 'Capacitación',
      flex: 1,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return <><RenderInputByTypeMobile
          input={{
            disableTypography: true,
            displayName: `$${row?.capacitacion || 0}`,
            name: 'checkCapacitacion',
            type: 'check',
            disabled: disabledChecks?.disabledCapacitacion
          }}
          value={row?.checkCapacitacion}
          handleChange={(name, value) => handleCheck(name, value)} />
          {row?.cambioSonda}</>;
      }
    },
    {
      field: 'emergencia',
      headerName: 'Urgencia',
      flex: 1,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return <RenderInputByTypeMobile
          input={{
            disableTypography: true,
            displayName: `$${row?.emergencia || 0}`,
            name: 'checkEmergencia',
            type: 'check',
            disabled: disabledChecks?.disabledEmergencia
          }}
          value={row?.checkEmergencia}
          handleChange={(name, value) => handleCheck(name, value)} />;

      }
    },
    {
      field: 'extraDuracion',
      headerName: 'Ext. Duración',
      flex: 1,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        const valorExtraDuracion = row.extraDuracion === 0 ? 0 : visita.idTipoProfesional == ID_ENFERMERO ? row.extraDuracion : calculaPrecioExtraDuracion(visita.fechaVisita, visita.fechaFinUsuario, honorario?.honorarioPresencial, esCuidador, dataFrecuenciaByVisita?.frecuenciaByVisita?.frecuencia ?? 0);

        return <RenderInputByTypeMobile
          input={{
            disableTypography: true,
            displayName: `$${valorExtraDuracion || 0}`,
            name: 'checkExtraDuracion',
            type: 'check',
            disabled: disabledChecks?.disabledExtraDuracion
          }}
          value={row?.checkExtraDuracion}
          handleChange={(name, value) => handleCheck(name, value)} />;

      }
    },
    {
      field: 'total',
      headerName: 'Importe Total',
      flex: 1,
      valueGetter: (total) => { return `$${total || 0}`; }
    }];


  const [getHonorarios, { data: dataHonorarios, loading }] = useLazyQuery(GET_HONORARIOS_VISITA, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataHonorarios) return;
    const adaptData = adaptHonorariosVisita(dataHonorarios);
    setHonorario((prev) => ({
      ...prev,
      ...adaptData
    }));
  }, [dataHonorarios]);

  useEffect(() => {
    if (!visita?.idVisita || isNaN(visita?.idVisita)) return; else
      getHonorarios({ variables: { idVisita: visita?.idVisita } });
  }, [visita?.idVisita]);

  const columns = useMemo(() => generateColumnsDefaultConfiguration(columnsBase), [disabledChecks, visita, honorario, dataFrecuenciaByVisita]);

  const rows = useMemo(() => {
    if (!honorario) return [];
    return [honorario];
  }, [honorario]);

  return (
    <DataGridGenerico
      columns={columns}
      rows={rows}
      autoMaxWidth
      totalCount={!!honorario ? 1 : 0}
      toolbar={false}
      loading={loading} />);
};